
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil, timer } from 'rxjs';
import { TranslationService } from '../../services/translation.service';
import { LanguageConfig } from '../../models/Translation';
import { SUPPORTED_LANGUAGES } from '../../config/translation.config';

@Component({
    selector: 'app-language-selector',
    standalone: true,
    imports: [],
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.scss',
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
    availableLanguages: LanguageConfig[] = SUPPORTED_LANGUAGES;
    currentLanguage: LanguageConfig | undefined;
    isDropdownOpen = false;
    isLoading = false;
    hasError = false;
    errorMessage = '';
    successMessage = '';
    showFeedback = false;
    private destroy$ = new Subject<void>();

    constructor(private translationService: TranslationService) {}

    ngOnInit(): void {
        // Subscribe to current language changes
        this.translationService.currentLanguage$
            .pipe(takeUntil(this.destroy$))
            .subscribe((languageCode) => {
                this.currentLanguage = this.availableLanguages.find(
                    (lang) => lang.code === languageCode
                );
            });

        // Subscribe to loading state
        this.translationService.translationLoading$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isLoading) => {
                console.log(
                    `[LanguageSelector] Loading state changed: ${isLoading}`
                );
                this.isLoading = isLoading;

                // Clear error when starting to load
                if (isLoading) {
                    this.hasError = false;
                    this.errorMessage = '';
                    this.clearFeedback();
                }
            });

        // Subscribe to translation errors
        this.translationService.translationError$
            .pipe(takeUntil(this.destroy$))
            .subscribe((error) => {
                console.log(`[LanguageSelector] Error state changed: ${error}`);
                if (error) {
                    this.hasError = true;
                    this.errorMessage = error;
                    this.showUserFeedback(error, 'error');
                } else {
                    this.hasError = false;
                    this.errorMessage = '';
                }
            });

        // Set initial language
        const currentLangCode = this.translationService.getCurrentLanguage();
        this.currentLanguage = this.availableLanguages.find(
            (lang) => lang.code === currentLangCode
        );
    }

    ngOnDestroy(): void {
        console.log(`[LanguageSelector] Component being destroyed`);
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Toggle dropdown visibility
     */
    toggleDropdown(): void {
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    /**
     * Close dropdown
     */
    closeDropdown(): void {
        this.isDropdownOpen = false;
    }

    /**
     * Select a language
     */
    selectLanguage(language: LanguageConfig): void {
        console.log(
            `[LanguageSelector] selectLanguage called with: ${language.code}, current: ${this.currentLanguage?.code}`
        );

        if (language.code !== this.currentLanguage?.code) {
            console.log(
                `[LanguageSelector] Language change detected, proceeding with change`
            );

            // Clear any previous feedback
            this.clearFeedback();

            // Set the language (this will trigger loading state)
            this.translationService.setLanguage(language.code);

            // Use the improved approach that checks for actual language change
            this.waitForLanguageChangeAndReload(language.code);
        } else {
            console.log(
                `[LanguageSelector] Same language selected, no change needed`
            );
        }
        this.closeDropdown();
    }

    /**
     * Handle click outside to close dropdown
     */
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        const languageSelector = target.closest('.language-selector');

        if (!languageSelector) {
            this.closeDropdown();
        }
    }

    /**
     * Show user feedback message
     */
    private showUserFeedback(message: string, type: 'success' | 'error'): void {
        if (type === 'success') {
            this.successMessage = message;
        } else {
            this.errorMessage = message;
        }

        this.showFeedback = true;

        // Auto-hide feedback after 3 seconds
        timer(3000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.clearFeedback();
            });
    }

    /**
     * Clear feedback messages
     */
    private clearFeedback(): void {
        this.showFeedback = false;
        this.successMessage = '';
        // Don't clear error message here as it's managed by the error observable
    }

    /**
     * Manually dismiss feedback
     */
    dismissFeedback(): void {
        this.clearFeedback();

        // Also clear translation service error if it's an error feedback
        if (this.hasError) {
            this.translationService.clearError();
        }
    }

    /**
     * Retry language loading
     */
    retryLanguageLoad(): void {
        if (this.currentLanguage) {
            this.clearFeedback();
            this.translationService.setLanguage(this.currentLanguage.code);
        }
    }

    /**
     * Attempt recovery from translation errors
     */
    attemptRecovery(): void {
        this.clearFeedback();
        this.translationService.attemptRecovery().subscribe({
            next: (success) => {
                if (success) {
                    this.showUserFeedback(
                        'Translation recovery successful',
                        'success'
                    );
                } else {
                    this.showUserFeedback(
                        'Recovery failed. Using fallback language.',
                        'error'
                    );
                }
            },
            error: () => {
                this.showUserFeedback(
                    'Recovery failed. Please refresh the page.',
                    'error'
                );
            },
        });
    }

    /**
     * Wait for language change to be saved and then reload (alternative to simple timeout)
     */
    private waitForLanguageChangeAndReload(targetLanguageCode: string): void {
        console.log(
            `[LanguageSelector] Waiting for language change to: ${targetLanguageCode}`
        );

        let attempts = 0;
        const maxAttempts = 10;
        const checkInterval = 200; // Check every 200ms

        const checkLanguageChange = () => {
            attempts++;
            const storedPreference = localStorage.getItem(
                'bedici_language_preference'
            );
            const currentLang = this.translationService.getCurrentLanguage();

            console.log(
                `[LanguageSelector] Check attempt ${attempts}: stored=${storedPreference}, current=${currentLang}, target=${targetLanguageCode}`
            );

            // Check if language preference was saved or service language changed
            const languageChanged =
                (storedPreference &&
                    storedPreference.includes(targetLanguageCode)) ||
                currentLang === targetLanguageCode;

            if (languageChanged) {
                console.log(
                    `[LanguageSelector] Language change confirmed, reloading...`
                );
                window.location.reload();
                return;
            }

            // If we haven't reached max attempts, try again
            if (attempts < maxAttempts) {
                setTimeout(checkLanguageChange, checkInterval);
            } else {
                // Fallback: reload anyway after max attempts
                console.log(
                    `[LanguageSelector] Max attempts reached, forcing reload`
                );
                window.location.reload();
            }
        };

        // Start checking after a small delay to allow the service to process
        setTimeout(checkLanguageChange, checkInterval);
    }
}
